import FormikTextAreaInput from "@components/FormikTextAreaInput";
import FormikTextInput from "@components/FormikTextInput";
import Api from "@services/Api";
import { RootState, useAppDispatch } from "@services/Redux";
import { fetchOrder } from "@services/Redux/order";
import { Money } from "@utils/currency";
import { toastError, toastMessage } from "@utils/functions";
import { CnpjDiscountType, DocumentType, ShippingMethod } from "@utils/types";
import axios from "axios";
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from "formik";
import LogRocket from "logrocket";
import * as React from "react";
import { useCookies } from "react-cookie";
import ReactGA from "react-ga4";
import { IoChevronBack } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import PulseLoader from "react-spinners/PulseLoader";
import OrderValueSummary from "./OrderValueSummary";
import Styled from "./styled";
import { CustomerDataFormValues } from "./types";
import states from "@utils/states.json";
import statesWithCities from "@utils/estados-cidades.json";
import FormikSelect from "@components/FormikSelect";
import MetaPixel from "@utils/meta/MetaPixel";

const CheckoutScreen: React.FC = () => {
  const companyData = useSelector((state: RootState) => state.company.data);
  const { user: userData } = useSelector((state: RootState) => state.app);
  const order = useSelector((state: RootState) => state.order.data);
  const formRef = React.useRef<FormikProps<CustomerDataFormValues>>(null);
  const [couponInputText, setCouponInputText] = React.useState("");
  const [loadingCoupon, setLoadingCoupon] = React.useState(false);
  const [cookies] = useCookies([`company_${companyData.code}_seller_id`]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    //dispatch(fetchUserData());
  }, []);

  React.useEffect(() => {
    // Checking for user data change
    if (userData) {
      formRef?.current?.resetForm({
        values: {
          state: userData?.address?.state ?? "",
          city: userData?.address?.city ?? "",
          neighborhood: userData?.address?.neighborhood,
          street: userData?.address?.street,
          number: userData?.address?.number ?? "",
          zip_code: userData?.address?.zip_code ?? "",
          name: userData?.name ?? "",
          email: userData?.email ?? "",
          phone: userData?.phone ?? "",
          cash_payment: false,
          observations: "",
          document_type: "",
          document_number: "",
          birthdate: userData.birthdate ?? "",
        },
      });
    }
  }, [userData]);

  const handleBackButtonClick = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleSubmit = async (values: CustomerDataFormValues, actions: FormikHelpers<CustomerDataFormValues>) => {
    try {
      LogRocket.identify(values.email, {
        name: values.name,
        email: values.email,
      });
    } catch (error) {
      console.log(`Error setting logrocket user`);
    }
    try {
      if (!order) return;
      actions.setSubmitting(true);

      let sellerId = localStorage.getItem(`company_${companyData.code}_seller_id`);
      if (!sellerId) {
        sellerId = cookies[`company_${companyData.code}_seller_id`];
      }

      const { data } = await Api.post(`/order/${order.id}/finish`, {
        ...values,
        seller_id: localStorage.getItem(`company_${companyData.code}_seller_id`),
      });

      const source = localStorage.getItem("source");

      ReactGA.gtag("event", "purchase", {
        transaction_id: order.id,
        currency: "BRL",
        shipping: order.shipment_value ?? 0,
        value: order.value,
        affiliation: source,
        company_name: companyData.name,
        items: order.items.map((item) => ({
          item_id: item.product.id,
          item_name: item.product.name,
          price: item.price,
          quantity: item.quantity,
          item_category: item.product.categories?.[0].name,
          item_variant: `${item.variation?.color?.name}`,
          size: item.variation?.size?.name,
        })),
      });
      MetaPixel.trackCustom("purchase", {
        transaction_id: order.id,
        currency: "BRL",
        shipping: order.shipment_value ?? 0,
        value: order.value,
        affiliation: source,
        company_name: companyData.name,
        items: order.items.map((item) => ({
          item_id: item.product.id,
          item_name: item.product.name,
          price: item.price,
          quantity: item.quantity,
          item_category: item.product.categories?.[0].name,
          item_variant: `${item.variation?.color?.name}`,
          size: item.variation?.size?.name,
        })),
      });
      dispatch(fetchOrder());
      actions.setSubmitting(false);
      if (data.emepag_order_url) {
        navigate("/emepag_redirect?url=" + encodeURIComponent(data.emepag_order_url));
      } else {
        if (window.history.state && window.history.state.idx > 0) {
          navigate(-1);
        } else {
          navigate("/sacola", { replace: true });
        }
      }
    } catch (error) {
      actions.setSubmitting(false);
      console.log(error, "error");
      toastError(error);
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    formRef.current?.setFieldValue("zip_code", e.target.value);
    if (/^[0-9]{5}-[0-9]{3}$/.test(e.target.value)) {
      try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${e.target.value}/json/`);
        // If data.logradouro exists, it means that the CEP was found
        if (data.logradouro) {
          formRef.current?.setFieldValue("street", data.logradouro);
          formRef.current?.setFieldValue("neighborhood", data.bairro);
          formRef.current?.setFieldValue("city", data.localidade);
          formRef.current?.setFieldValue("state", data.uf);
        }
      } catch (error) {}
    }
  };

  const handleApplyCoupon = async () => {
    setLoadingCoupon(true);
    if (!couponInputText) {
      setLoadingCoupon(false);
      return toastError("Digite um cupom");
    }
    try {
      await Api.post(`/order/${order?.code}/coupon/${couponInputText}/apply`);
      dispatch(fetchOrder());
      toastMessage("Cupom aplicado", "success");
      setLoadingCoupon(false);
    } catch (error) {
      setLoadingCoupon(false);
      toastError(error);
    }
  };

  const validate = (values: CustomerDataFormValues) => {
    let errors: FormikErrors<CustomerDataFormValues> = {};
    const phoneRegex = /\([1-9]\d\)9\d{4}-\d{4}/g;

    if (!values.name) {
      errors.name = "Dado obrigatório";
    } else if (!values.phone) {
      errors.phone = "Dado obrigatório";
    } else if (!phoneRegex.test(values.phone)) {
      errors.phone = "Telefone inválido";
    } else if ((!values.document_type || !values.document_number) && companyData.requires_document) {
      errors.document_type = "Dado obrigatório";
      errors.document_number = "Dado obrigatório";
    }
    return errors;
  };

  const initialValues: CustomerDataFormValues = {
    name: userData?.name ?? "",
    email: userData?.email ?? "",
    cash_payment: false,
    document_type: "",
    document_number: "",
    phone: userData?.phone ?? "",
    observations: "",
    shipping_method: ShippingMethod.EXCURSION,
    excursion_address: "",
    excursion_address_reference: "",
    excursion_name: "",
    excursion_phone: "",
    shipping_observations: "",
    city: userData?.address?.city ?? "",
    state: userData?.address?.state ?? "",
    zip_code: userData?.address?.zip_code ?? "",
    street: userData?.address?.street ?? "",
    number: userData?.address?.number ?? "",
    neighborhood: userData?.address?.neighborhood ?? "",
    complement: userData?.address?.complement ?? "",
    birthdate: "",
  };

  console.log(initialValues, "initialValues");

  return (
    <Styled.RootContainer>
      {/* Header */}
      <Styled.HeaderContainer className={"border-bottom"}>
        <Styled.HeaderCloseButtonContainer onClick={handleBackButtonClick}>
          <IoChevronBack size={26} color={"black"} />
        </Styled.HeaderCloseButtonContainer>
        <Styled.HeaderTitle>Forneça seus dados</Styled.HeaderTitle>
      </Styled.HeaderContainer>
      {/* End of Header */}
      {/* Content */}
      <Styled.ContentContainer>
        {/* Discounts */}
        {companyData.cnpj_discount_percentage || companyData.cnpj_discount_value || companyData.cash_payment_discount_percentage ? (
          <div className={"d-flex align-items-center flex-column mt-2"}>
            {companyData.cash_payment_discount_percentage > 0 && (
              <Styled.ValueWithDiscount>
                <span style={{ fontSize: 12 }}> {companyData.cash_payment_discount_percentage}% de desconto à vista</span>
              </Styled.ValueWithDiscount>
            )}
            {companyData.cnpj_discount_type === CnpjDiscountType.FIXED && (
              <Styled.ValueWithDiscount>
                <span style={{ fontSize: 12 }}> {Money(companyData.cnpj_discount_value ?? 0).format()} de desconto para CNPJ</span>
              </Styled.ValueWithDiscount>
            )}
            {companyData.cnpj_discount_type === CnpjDiscountType.PERCENTAGE && (
              <Styled.ValueWithDiscount>
                <span style={{ fontSize: 12 }}> {companyData.cnpj_discount_percentage}% de desconto para CNPJ</span>
              </Styled.ValueWithDiscount>
            )}
          </div>
        ) : null}
        {/* End of Discounts */}
        {/* Customer data form */}
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate} innerRef={formRef}>
          {({ values, setFieldValue, errors, isSubmitting }) => (
            <Form className={"row p-0 m-0 no-gutters mt-4"}>
              {/* Customer data */}
              <div className={"col-12 border-top py-3"}>
                <h4>Dados pessoais</h4>
              </div>
              <div className={"col-12"}>
                <FormikTextInput label="Nome completo" name="name" mask="" placeholder="Nome" required />
              </div>
              <div className={"col-12 mt-3"}>
                <FormikTextInput label="Seu email" name="email" type={"email"} mask="" placeholder="Digite seu email" />
              </div>
              <div className={"col-12 mt-3"}>
                <FormikTextInput label="Telefone" name="phone" mask="(99)99999-9999" placeholder="(__) _____-____" required />
              </div>
              <div className={"col-12 mt-3"}>
                <label>Tipo de documento</label>
                <div className={"d-flex flex-row"}>
                  <Styled.ToggleButton
                    type="button"
                    active={values.document_type === DocumentType.CPF}
                    onClick={(e) => {
                      e.preventDefault();
                      setFieldValue("document_type", "cpf");
                    }}
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  >
                    CPF
                  </Styled.ToggleButton>
                  <Styled.ToggleButton
                    type="button"
                    active={values.document_type === DocumentType.CNPJ}
                    onClick={(e) => {
                      e.preventDefault();
                      setFieldValue("document_type", "cnpj");
                    }}
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    CNPJ
                  </Styled.ToggleButton>
                </div>
                {errors.document_type && <div className={"text-danger"}>{errors.document_type}</div>}
              </div>
              {values.document_type && (
                <div className={"col-12 mt-3"}>
                  {values.document_type === DocumentType.CPF ? (
                    <FormikTextInput label="CPF" name="document_number" mask="999.999.999-99" placeholder="Digite seu CPF" required={companyData.requires_document} />
                  ) : (
                    <FormikTextInput
                      label="CNPJ"
                      name="document_number"
                      mask="99.999.999/9999-99"
                      placeholder="Digite seu CNPJ"
                      required={companyData.requires_document}
                    />
                  )}
                </div>
              )}
              {companyData.requires_birthdate ? (
                <div className={"col-12 mt-3"}>
                  <FormikTextInput name="birthdate" label={"Data de nascimento"} mask="99/99/9999" required />
                </div>
              ) : null}
              {/* End of customer data */}

              {/* Shipping data */}
              {companyData.is_shipment_enabled && <></>}
              {companyData.is_shipment_enabled && (
                <>
                  <div className={"col-12 mt-3"}>
                    <label>Forma de envio</label>
                    <div className={"d-flex flex-column"}>
                      <Styled.ShippingMethodContainer
                        onClick={() => {
                          setFieldValue("shipping_method", ShippingMethod.EXCURSION);
                        }}
                        active={values.shipping_method === ShippingMethod.EXCURSION}
                      >
                        <div>
                          <b>Excursão</b>
                        </div>
                      </Styled.ShippingMethodContainer>
                      {companyData.is_pickup_enabled && (
                        <Styled.ShippingMethodContainer
                          onClick={() => {
                            setFieldValue("shipping_method", ShippingMethod.PICKUP);
                          }}
                          active={values.shipping_method === ShippingMethod.PICKUP}
                        >
                          <div>
                            <b>Retirar em mãos</b>
                          </div>
                        </Styled.ShippingMethodContainer>
                      )}
                      <Styled.ShippingMethodContainer
                        onClick={() => {
                          setFieldValue("shipping_method", ShippingMethod.CORREIOS);
                        }}
                        active={values.shipping_method === ShippingMethod.CORREIOS}
                      >
                        <div>
                          <b>Correios</b>
                        </div>
                      </Styled.ShippingMethodContainer>
                      <Styled.ShippingMethodContainer
                        onClick={() => {
                          setFieldValue("shipping_method", ShippingMethod.SHIPPING_COMPANY);
                        }}
                        active={values.shipping_method === ShippingMethod.SHIPPING_COMPANY}
                      >
                        <div>
                          <b>Transportadora</b>
                        </div>
                      </Styled.ShippingMethodContainer>
                    </div>
                  </div>
                  {![ShippingMethod.PICKUP, ShippingMethod.EXCURSION].includes(values.shipping_method as ShippingMethod) || companyData.requires_customer_address ? (
                    <>
                      <div className={"col-12 border-top pt-3 mt-3"}>
                        <h4>Dados de entrega</h4>
                      </div>
                      <div className={"col-12"}>
                        <FormikTextInput name="zip_code" label={"CEP"} mask="99999-999" value={values.zip_code} onChange={handleCepChange} required />
                      </div>
                      <div className={"col-12"}>
                        <FormikTextInput name="street" label={"Rua"} mask="" required />
                      </div>
                      <div className={"col-12"}>
                        <FormikTextInput name="number" label={"Número"} mask="" required />
                      </div>
                      <div className={"col-12"}>
                        <FormikTextInput name="neighborhood" label={"Bairro"} mask="" required />
                      </div>
                      <div className={"col-12"}>
                        <FormikSelect
                          name="state"
                          label={"Estado"}
                          value={values.state}
                          options={states.map((state) => ({ value: state.short_name, label: state.full_name }))}
                          required
                        />
                      </div>
                      <div className={"col-12"}>
                        <FormikSelect
                          name="city"
                          label={"Cidade"}
                          options={statesWithCities.estados.find((s) => s.sigla === values.state)?.cidades.map((c) => ({ value: c, label: c })) ?? []}
                          value={values.city}
                          required
                        />
                      </div>
                      <div className={"col-12"}>
                        <FormikTextInput name="complement" label={"Complemento"} mask="" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={"col-12"}>
                        <FormikSelect
                          name="state"
                          label={"Estado"}
                          options={[
                            {
                              value: "",
                              label: "Selecione seu estado",
                            },
                            ...states.map((state) => ({ value: state.short_name, label: state.full_name })),
                          ]}
                          required
                        />
                      </div>
                      <div className={"col-12"}>
                        <FormikSelect
                          name="city"
                          label={"Cidade"}
                          disabled={!values.state}
                          options={[
                            {
                              value: "",
                              label: values.state ? "Selecione sua cidade" : "Selecione seu estado antes",
                            },
                            ...(statesWithCities.estados.find((s) => s.sigla === values.state)?.cidades.map((c) => ({ value: c, label: c })) ?? []),
                          ]}
                          required
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {values.shipping_method === ShippingMethod.EXCURSION && companyData.is_shipment_enabled && (
                <>
                  <div className={"col-12"}>
                    <FormikTextInput name="excursion_name" label={"Nome da excursão"} mask="" required={companyData.is_excursion_info_required} />
                  </div>
                  <div className={"col-12"}>
                    <FormikTextInput name="excursion_address" label={"Endereço da excursão"} mask="" required={companyData.is_excursion_info_required} />
                  </div>
                  <div className={"col-12"}>
                    <FormikTextInput name="excursion_phone" label={"Telefone da excursão"} mask="(99)99999-9999" required={companyData.is_excursion_info_required} />
                  </div>
                  <div className={"col-12"}>
                    <FormikTextInput
                      name="excursion_address_reference"
                      label={"Ponto de referência da excursão"}
                      mask=""
                      required={companyData.is_excursion_info_required}
                    />
                  </div>
                </>
              )}

              {/* End of shipping data */}

              {companyData.cash_payment_discount_percentage > 0 && (
                <div className={"col-12 mt-3"}>
                  <label>Pagamento</label>
                  <div className={"d-flex flex-row"}>
                    <Styled.ToggleButton
                      type="button"
                      active={values.cash_payment}
                      onClick={(e) => {
                        e.preventDefault();
                        setFieldValue("cash_payment", true);
                      }}
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      À vista
                    </Styled.ToggleButton>
                    <Styled.ToggleButton
                      type="button"
                      active={!values.cash_payment}
                      onClick={(e) => {
                        e.preventDefault();
                        setFieldValue("cash_payment", false);
                      }}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      Crédito
                    </Styled.ToggleButton>
                  </div>
                </div>
              )}

              <div className={"col-12 mt-3"}>
                <FormikTextAreaInput
                  label="Observações"
                  name="observations"
                  placeholder="Insira observações sobre excursões, pagamento, ou qualquer outra informação."
                  style={{ minHeight: 100 }}
                />
              </div>

              <div className={"col-9 mt-3"}>
                <label>Cupom</label>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={couponInputText}
                  onChange={(e) => {
                    setCouponInputText(e.target.value);
                  }}
                  disabled={loadingCoupon}
                />
              </div>
              <div className={"col-3 mt-3 d-flex align-items-end"}>
                <button
                  className={"btn btn-dark w-100"}
                  disabled={loadingCoupon || isSubmitting || !couponInputText}
                  onClick={(e) => {
                    e.preventDefault();
                    handleApplyCoupon();
                  }}
                >
                  {loadingCoupon ? <PulseLoader color={"white"} size={5} /> : "Aplicar"}
                </button>
              </div>

              <OrderValueSummary />

              <div className={"col-12"}>
                <Styled.FinishButton type="submit" disabled={isSubmitting || loadingCoupon}>
                  <span>Fechar meu pedido</span>
                </Styled.FinishButton>
              </div>
            </Form>
          )}
        </Formik>
        {/* End of Customer data form */}
      </Styled.ContentContainer>
      {/* End of Content */}
    </Styled.RootContainer>
  );
};

export default CheckoutScreen;
